import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
  Type,
  Palette,
  Undo,
  Redo,
  Sparkles,
  Loader2,
  CheckCircle2,
  X
} from 'lucide-react';

const HIGHLIGHT_COLORS = [
  { name: 'Yellow', value: '#fef08a', type: 'definition' },
  { name: 'Green', value: '#bbf7d0', type: 'formula' },
  { name: 'Blue', value: '#bfdbfe', type: 'key_term' },
  { name: 'Pink', value: '#fbcfe8', type: 'exam_hint' },
  { name: 'Orange', value: '#fed7aa', type: 'action_item' },
];

const TEXT_COLORS = [
  { name: 'Default', value: 'inherit' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Green', value: '#16a34a' },
  { name: 'Purple', value: '#9333ea' },
  { name: 'Orange', value: '#ea580c' },
];

export default function RichTextEditor({
  content,
  onChange,
  suggestions = [],
  onApplySuggestion,
  placeholder = 'Start typing...',
  editable = true,
  className = ''
}) {
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [appliedSuggestions, setAppliedSuggestions] = useState(new Set());

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextStyle,
      Color,
      Underline,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: content || '',
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  // Update content when it changes externally
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const applyHighlight = useCallback((color) => {
    if (editor) {
      editor.chain().focus().toggleHighlight({ color }).run();
    }
  }, [editor]);

  const applyTextColor = useCallback((color) => {
    if (editor) {
      if (color === 'inherit') {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
      }
    }
  }, [editor]);

  const applySuggestion = useCallback((suggestion, index) => {
    if (!editor) return;

    const content = editor.getText();
    const searchText = suggestion.text;
    
    // Find the text in the editor
    const startIndex = content.indexOf(searchText);
    if (startIndex === -1) {
      // Try case-insensitive search
      const lowerContent = content.toLowerCase();
      const lowerSearch = searchText.toLowerCase();
      const altIndex = lowerContent.indexOf(lowerSearch);
      if (altIndex === -1) return;
    }

    // Get the highlight color based on suggestion type
    const colorMap = {
      definition: '#fef08a',
      formula: '#bbf7d0',
      key_term: '#bfdbfe',
      exam_hint: '#fbcfe8',
      action_item: '#fed7aa',
    };
    const color = colorMap[suggestion.type] || suggestion.color || '#fef08a';

    // Apply highlight using editor's search and replace
    // First, select all then search for the text
    editor.commands.setTextSelection({ from: 0, to: editor.state.doc.content.size });
    
    // Try to find and select the specific text
    const { doc } = editor.state;
    let found = false;
    doc.descendants((node, pos) => {
      if (found || !node.isText) return;
      const text = node.text || '';
      const idx = text.indexOf(searchText);
      if (idx !== -1) {
        editor.commands.setTextSelection({ from: pos + idx, to: pos + idx + searchText.length });
        editor.chain().focus().setHighlight({ color }).run();
        found = true;
      }
    });

    // Mark suggestion as applied
    setAppliedSuggestions(prev => new Set([...prev, index]));
    
    if (onApplySuggestion) {
      onApplySuggestion(suggestion, index);
    }
  }, [editor, onApplySuggestion]);

  const applyAllSuggestions = useCallback(() => {
    suggestions.forEach((suggestion, index) => {
      if (!appliedSuggestions.has(index)) {
        setTimeout(() => applySuggestion(suggestion, index), index * 100);
      }
    });
  }, [suggestions, appliedSuggestions, applySuggestion]);

  if (!editor) {
    return null;
  }

  const filteredSuggestions = suggestions.filter((_, i) => !appliedSuggestions.has(i));

  return (
    <div className={`rich-text-editor ${className}`}>
      {/* Toolbar */}
      {editable && (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/30 rounded-t-lg">
          {/* Text Formatting */}
          <Button
            variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().toggleBold().run()}
            data-testid="bold-btn"
          >
            <Bold className="w-4 h-4" />
          </Button>
          <Button
            variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            data-testid="italic-btn"
          >
            <Italic className="w-4 h-4" />
          </Button>
          <Button
            variant={editor.isActive('underline') ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            data-testid="underline-btn"
          >
            <UnderlineIcon className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Highlight Colors */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="highlight-btn">
                <Highlighter className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2">
              <div className="flex gap-1">
                {HIGHLIGHT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    className="w-6 h-6 rounded-full border-2 border-transparent hover:border-foreground/50 transition-colors"
                    style={{ backgroundColor: color.value }}
                    onClick={() => applyHighlight(color.value)}
                    title={color.name}
                  />
                ))}
                <button
                  className="w-6 h-6 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center hover:border-foreground/50"
                  onClick={() => editor.chain().focus().unsetHighlight().run()}
                  title="Remove highlight"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#fef08a' }} /> Definitions
                <span className="inline-block w-3 h-3 rounded ml-2" style={{ backgroundColor: '#bbf7d0' }} /> Formulas
                <span className="inline-block w-3 h-3 rounded ml-2" style={{ backgroundColor: '#bfdbfe' }} /> Key Terms
              </div>
            </PopoverContent>
          </Popover>

          {/* Text Color */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="text-color-btn">
                <Palette className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2">
              <div className="flex gap-1">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    className="w-6 h-6 rounded-full border-2 border-transparent hover:border-foreground/50 transition-colors flex items-center justify-center"
                    style={{ backgroundColor: color.value === 'inherit' ? 'transparent' : color.value }}
                    onClick={() => applyTextColor(color.value)}
                    title={color.name}
                  >
                    {color.value === 'inherit' && <Type className="w-3 h-3" />}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Undo/Redo */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Editor Content */}
      <div className="relative">
        <EditorContent 
          editor={editor} 
          className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none"
        />
      </div>

      {/* AI Suggestions Panel */}
      {suggestions.length > 0 && showSuggestions && (
        <div className="border-t border-border bg-primary/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">AI Study Suggestions</span>
              <span className="text-xs text-muted-foreground">({filteredSuggestions.length} remaining)</span>
            </div>
            <div className="flex gap-2">
              {filteredSuggestions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={applyAllSuggestions}
                  className="h-7 text-xs"
                  data-testid="apply-all-suggestions-btn"
                >
                  Apply All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSuggestions(false)}
                className="h-7 text-xs"
              >
                Hide
              </Button>
            </div>
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {suggestions.map((suggestion, index) => {
              const isApplied = appliedSuggestions.has(index);
              const colorMap = {
                definition: '#fef08a',
                formula: '#bbf7d0',
                key_term: '#bfdbfe',
                exam_hint: '#fbcfe8',
                action_item: '#fed7aa',
              };
              const bgColor = colorMap[suggestion.type] || '#fef08a';
              
              return (
                <div
                  key={index}
                  className={`flex items-start gap-2 p-2 rounded-lg text-sm transition-opacity ${
                    isApplied ? 'opacity-50' : 'bg-background/50'
                  }`}
                >
                  <div
                    className="w-3 h-3 rounded mt-1 flex-shrink-0"
                    style={{ backgroundColor: bgColor }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">"{suggestion.text}"</p>
                    <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => applySuggestion(suggestion, index)}
                    disabled={isApplied}
                    data-testid={`apply-suggestion-${index}`}
                  >
                    {isApplied ? (
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Collapsed suggestions indicator */}
      {suggestions.length > 0 && !showSuggestions && filteredSuggestions.length > 0 && (
        <button
          onClick={() => setShowSuggestions(true)}
          className="w-full p-2 text-sm text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2 border-t border-border"
        >
          <Sparkles className="w-4 h-4" />
          Show {filteredSuggestions.length} AI suggestions
        </button>
      )}
    </div>
  );
}
